/**
 * StoryHomePage组件 - 互动故事场景选择页面
 * 功能：
 * 1. 显示预设故事场景列表
 * 2. 支持选择预设场景或自定义故事
 * 3. 初始化故事会话并跳转到会话页面
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { storyAPI } from '../services/storyAPI';
import { addTouchOptimization, getDeviceType } from '../utils/responsive';
import SidebarNav from './common/SidebarNav';

const StoryHomePage = () => {
  // 状态管理
  const [publishedItems, setPublishedItems] = useState([]); // 已发布故事
  const [draftItems, setDraftItems] = useState([]); // 草稿故事（最近会话）
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const isRequesting = useRef(false); // 防止重复请求

  /**
   * 获取剧情库列表（默认仅已发布）
   */
  const fetchLibrary = async () => {
    // 防止重复请求
    if (isRequesting.current) {
      console.log('🔄 请求正在进行中，跳过重复请求');
      return;
    }

    console.log('🔄 开始获取剧情库列表...');
    isRequesting.current = true;
    setLoading(true);
    setError(null);

    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('请求超时，请检查网络连接')), 30000);
      });

      // 并行获取 published 和 draft 故事
      const [publishedResponse, draftResponse] = await Promise.race([
        Promise.all([
          storyAPI.getStoryLibrary({ status: 'published', page: 1, page_size: 50 }),
          storyAPI.getStoryLibrary({ status: 'draft', page: 1, page_size: 50 })
        ]),
        timeoutPromise
      ]);

      if (publishedResponse && publishedResponse.data && publishedResponse.data.success) {
        console.log('✅ 获取已发布故事成功:', publishedResponse.data.items.length, '条');
        setPublishedItems(publishedResponse.data.items || []);
      }

      if (draftResponse && draftResponse.data && draftResponse.data.success) {
        console.log('✅ 获取草稿故事成功:', draftResponse.data.items.length, '条');
        // 过滤掉未开始交互的（无视频）
        const validDrafts = (draftResponse.data.items || []).filter(item => 
          (item.episodes_count || 0) > 0
        );
        setDraftItems(validDrafts || []);
      }
    } catch (err) {
      console.error('❌ 获取剧情库失败:', err);
      setError('获取剧情库失败: ' + (err.response?.data?.detail || err.message));
      setPublishedItems([]);
      setDraftItems([]);
    } finally {
      console.log('🏁 剧情库获取完成');
      setLoading(false);
      isRequesting.current = false;
    }
  };


  // 组件挂载时获取剧情库列表
  useEffect(() => {
    console.log('🔄 故事首页加载，路径:', location.pathname);
    if (location.pathname === '/story') {
      fetchLibrary();
    }
  }, [location.pathname]);

  // 响应式优化
  useEffect(() => {
    const deviceType = getDeviceType();
    if (deviceType === 'mobile' && (publishedItems.length > 0 || draftItems.length > 0)) {
      const scenarioCards = document.querySelectorAll('.scenario-card');
      scenarioCards.forEach(card => {
        addTouchOptimization(card);
      });
    }
  }, [publishedItems, draftItems]);

  // 加载状态
  if (loading) {
    return (
      <div className="App">
        <SidebarNav />
        <div className="main-layout">
          <div className="content-area">
            <div className="container">
              <div className="loading">
                <div className="loading-spinner"></div>
              <h2>正在加载剧情库...</h2>
              <p>请稍候，我们正在为您准备精彩内容</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 主页面渲染
  return (
    <div className="App">
      <SidebarNav />
      <div className="main-layout">
        <div className="content-area">
          <div className="container">
            {/* 错误信息显示 */}
            {error && (
              <div className="error" style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '10px' }}>{error}</div>
                <button
                  className="btn btn-primary"
                  onClick={fetchLibrary}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: '#007bff',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  🔄 重新加载
                </button>
              </div>
            )}


            {/* 主内容区：左右布局 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '3fr 1fr',
              gap: '24px',
              alignItems: 'start'
            }}>
              {/* 左侧：剧情库网格布局（视频为主体，标题与文字悬浮其上） */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 20
              }}>
              {publishedItems.length > 0 ? (
                publishedItems.map((item) => (
                  <div
                    key={item.id}
                    className="scenario-card"
                    onClick={() => navigate(`/story/interact/${item.id}`)}
                    style={{
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      borderRadius: 12,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      background: '#000',
                      border: '1px solid #333',
                      width: '100%',
                      aspectRatio: '9 / 16', // 竖屏视频比例 9:16
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {/* 视频主体（全幅）+ 顶部/底部悬浮信息层 */}
                    <div style={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden',
                      background: '#000'
                    }}>
                      {(item.video_url) && (
                        <video
                          src={item.video_url}
                          preload="auto"
                          muted
                          autoPlay
                          loop
                          playsInline
                          style={{ 
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            width: '100%', 
                            height: '100%', 
                            minWidth: '100%',
                            minHeight: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onError={(e) => {
                            console.error('视频加载失败:', item.video_url, e);
                            // 不隐藏视频，保持容器黑色背景
                          }}
                        />
                      )}

                      {/* 左上角标签：STORY */}
                      <div style={{ position: 'absolute', top: 12, left: 12, pointerEvents: 'none', zIndex: 10 }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '6px 10px',
                          borderRadius: 8,
                          background: 'rgba(0, 0, 0, 0.55)',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
                        }}>
                          STORY
                        </span>
                      </div>

                      {/* 右上角标签：new 或 连载到第 N 集 */}
                      <div style={{ position: 'absolute', top: 12, right: 12, pointerEvents: 'none', zIndex: 10 }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '6px 10px',
                          borderRadius: 8,
                          background: (item.episodes_count || 0) === 1 ? 'rgba(46, 204, 113, 0.75)' : 'rgba(231, 76, 60, 0.85)',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
                        }}>
                          {(item.episodes_count || 0) === 1 ? 'New' : `第${(item.episodes_count || 1) - 1}集`}
                        </span>
                      </div>

                      {/* 底部信息：标题 + 梗概（轻透明） */}
                      <div style={{
                        position: 'absolute', 
                        left: 0, 
                        right: 0, 
                        bottom: 0,
                        padding: '14px 16px',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0))',
                        color: '#fff', 
                        pointerEvents: 'none',
                        zIndex: 10
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 18 }}>{item.emoji || '📖'}</span>
                          <h3 style={{ margin: 0, fontSize: 20, lineHeight: 1.2 }}>{item.title}</h3>
                        </div>
                        <div style={{
                          fontSize: 13, lineHeight: 1.5, opacity: 0.95,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                        }}>
                          {item.subtitle}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
                  <p style={{ fontSize: '18px', color: '#666' }}>
                    暂无已发布剧情，请稍后再试
                  </p>
                  <button
                    onClick={fetchLibrary}
                    style={{
                      marginTop: '20px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: '#007bff',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    🔄 重新加载
                  </button>
                </div>
              )}
              </div>

              {/* 右侧栏：创建剧情按钮 + 最近会话列表 */}
              <div style={{
                position: 'sticky',
                top: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {/* 创建剧情按钮 */}
                <a 
                  href="/story/create" 
                  className="btn btn-primary" 
                  style={{ 
                    padding: '12px 16px', 
                    fontWeight: 700,
                    width: '100%',
                    textAlign: 'center',
                    display: 'block',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  ➕ 创建剧情
                </a>

                {/* 最近会话列表（草稿故事） */}
                {draftItems.length > 0 && (
                  <div style={{
                    maxHeight: 'calc(100vh - 120px)',
                    overflowY: 'auto',
                    paddingRight: '8px'
                  }}>
                    <div style={{
                      background: '#0b0b0b',
                      border: '1px solid #1f1f1f',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.35)'
                    }}>
                      <h2 style={{ 
                        fontSize: '18px', 
                        marginBottom: '16px', 
                        color: '#eaeaea',
                        fontWeight: 600,
                        borderBottom: '2px solid #2a2a2a',
                        paddingBottom: '12px'
                      }}>
                        📚 最新互动
                      </h2>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: '12px'
                      }}>
                        {draftItems.map((item) => (
                          <div
                            key={item.id}
                            onClick={() => navigate(`/story/interact/${item.id}`)}
                            style={{
                              padding: '12px',
                              background: '#141414',
                              border: '1px solid #2a2a2a',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#1a1a1a';
                              e.currentTarget.style.borderColor = '#4c6fff';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(76, 111, 255, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#141414';
                              e.currentTarget.style.borderColor = '#2a2a2a';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                              <h3 style={{ 
                                fontSize: '14px', 
                                fontWeight: 600, 
                                color: '#eaeaea',
                                margin: 0,
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                lineHeight: '1.4'
                              }}>
                                {item.title || `草稿 ${item.id}`}
                              </h3>
                            </div>
                            {item.subtitle && (
                              <p style={{ 
                                fontSize: '12px', 
                                color: '#9aa0a6', 
                                margin: '6px 0',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                lineHeight: '1.4'
                              }}>
                                {item.subtitle}
                              </p>
                            )}
                            <div style={{ 
                              fontSize: '11px', 
                              color: '#666',
                              marginTop: '8px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span>第 {item.episodes_count || 0} 集</span>
                              <span>{new Date(item.updated_at).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryHomePage;

